import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { adminFetch } from '../lib/api';
import { CHAPTER_TITLES_DE } from '@shared/chapters';

interface QuestionItem {
  id: string;
  chapter_id: number;
  question_text_de: string;
  image_url: string | null;
  image_hidden: boolean;
  image_reviewed: boolean;
  type: string;
}

const CHAPTER_NAMES = CHAPTER_TITLES_DE;

type FilterMode = 'unreviewed' | 'all' | 'with_image' | 'hidden' | 'visible';

export const ImageSweepPage: React.FC = () => {
  const [allQuestions, setAllQuestions] = useState<QuestionItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('unreviewed');
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null); // null = all chapters
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await adminFetch('/api/admin/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table: 'questions',
          operation: 'select',
          params: {
            select:
              'id, chapter_id, question_text_de, image_url, image_hidden, image_reviewed, type',
            order: { column: 'chapter_id', options: { ascending: true } },
          },
        }),
      });
      const json = await resp.json();
      if (json.error) throw new Error(json.error);
      setAllQuestions((json.data || []) as QuestionItem[]);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Filtered questions based on filter + chapter selection
  const questions = useMemo(() => {
    let filtered = allQuestions;

    // Apply image filter
    switch (filter) {
      case 'unreviewed':
        // Only show images that haven't been categorized yet:
        // - has an image
        // - not already hidden (= categorized as hidden)
        // - not marked as reviewed/kept in the DB
        filtered = filtered.filter(
          (q) => q.image_url && q.image_url.length > 10 && !q.image_hidden && !q.image_reviewed
        );
        break;
      case 'with_image':
        filtered = filtered.filter((q) => q.image_url && q.image_url.length > 10);
        break;
      case 'hidden':
        filtered = filtered.filter((q) => q.image_url && q.image_url.length > 10 && q.image_hidden);
        break;
      case 'visible':
        filtered = filtered.filter(
          (q) => q.image_url && q.image_url.length > 10 && !q.image_hidden
        );
        break;
      default:
        break;
    }

    // Apply chapter filter
    if (selectedChapter !== null) {
      filtered = filtered.filter((q) => q.chapter_id === selectedChapter);
    }

    return filtered;
  }, [allQuestions, filter, selectedChapter]);

  // Chapter stats for sidebar
  const chapterStats = useMemo(() => {
    const withImage = allQuestions.filter((q) => q.image_url && q.image_url.length > 10);
    const chapters = new Map<
      number,
      { total: number; hidden: number; visible: number; reviewed: number }
    >();

    for (const q of withImage) {
      const ch = q.chapter_id;
      if (!chapters.has(ch)) chapters.set(ch, { total: 0, hidden: 0, visible: 0, reviewed: 0 });
      const s = chapters.get(ch)!;
      s.total++;
      if (q.image_hidden) {
        s.hidden++;
        s.reviewed++;
      } else if (q.image_reviewed) {
        s.reviewed++;
      } else {
        s.visible++;
      }
    }

    return chapters;
  }, [allQuestions]);

  // Global stats
  const stats = useMemo(() => {
    const withImage = allQuestions.filter((q) => q.image_url && q.image_url.length > 10);
    const hidden = withImage.filter((q) => q.image_hidden).length;
    const reviewed = withImage.filter((q) => q.image_reviewed && !q.image_hidden).length;
    return {
      total: allQuestions.length,
      withImage: withImage.length,
      hidden,
      visible: withImage.filter((q) => !q.image_hidden).length,
      reviewed,
      unreviewed: withImage.length - hidden - reviewed,
    };
  }, [allQuestions]);

  // Reset index when filter/chapter changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [filter, selectedChapter]);

  const toggleImage = useCallback(
    async (hidden: boolean) => {
      const q = questions[currentIndex];
      if (!q || saving) return;
      setSaving(true);
      try {
        const resp = await adminFetch(`/api/questions/${q.id}/image-visibility`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hidden }),
        });
        const contentType = resp.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Server-Fehler: Unerwartete Antwort (${resp.status}).`);
        }
        const json = await resp.json();
        if (!resp.ok || !json.success) throw new Error(json.error || `Fehler (${resp.status})`);

        // Update in the master list (allQuestions) so it persists across navigation
        setAllQuestions((prev) =>
          prev.map((item) =>
            item.id === q.id ? { ...item, image_hidden: hidden, image_reviewed: true } : item
          )
        );

        // Auto-advance: in 'unreviewed' mode the list shrinks, so stay at same index
        // (next unreviewed item slides into current position).
        // In other modes, advance to next item.
        if (filter !== 'unreviewed' && hidden && currentIndex < questions.length - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else if (filter === 'unreviewed') {
          // Clamp index if we're at the end of a shrinking list
          setCurrentIndex((prev) =>
            Math.min(prev, questions.length - 2 >= 0 ? questions.length - 2 : 0)
          );
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setSaving(false);
      }
    },
    [currentIndex, questions, saving, filter]
  );

  // Mark as "kept" (reviewed but not hidden) — persisted to DB
  const keepImage = useCallback(async () => {
    const q = questions[currentIndex];
    if (!q || saving) return;
    setSaving(true);
    try {
      const resp = await adminFetch(`/api/questions/${q.id}/image-visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewed: true }),
      });
      const contentType = resp.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server-Fehler: Unerwartete Antwort (${resp.status}).`);
      }
      const json = await resp.json();
      if (!resp.ok || !json.success) throw new Error(json.error || `Fehler (${resp.status})`);

      // Update local state
      setAllQuestions((prev) =>
        prev.map((item) => (item.id === q.id ? { ...item, image_reviewed: true } : item))
      );
      // In 'unreviewed' mode the list shrinks — clamp index.
      // In other modes, advance normally.
      if (filter !== 'unreviewed' && currentIndex < questions.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (filter === 'unreviewed') {
        setCurrentIndex((prev) =>
          Math.min(prev, questions.length - 2 >= 0 ? questions.length - 2 : 0)
        );
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [currentIndex, questions, filter, saving]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a') {
        toggleImage(true);
      } else if (e.key === 'ArrowRight' || e.key === 'd') {
        keepImage();
      } else if (e.key === 'ArrowUp') {
        if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
      } else if (e.key === 'ArrowDown') {
        if (currentIndex < questions.length - 1) setCurrentIndex((prev) => prev + 1);
      } else if (e.key === 'u') {
        toggleImage(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleImage, keepImage, currentIndex, questions.length]);

  const current = questions[currentIndex];
  const progress =
    questions.length > 0 ? Math.round(((currentIndex + 1) / questions.length) * 100) : 0;

  // Get sorted chapter IDs
  const chapterIds = useMemo(() => {
    const ids = new Set(
      allQuestions.filter((q) => q.image_url && q.image_url.length > 10).map((q) => q.chapter_id)
    );
    return Array.from(ids).sort((a, b) => a - b);
  }, [allQuestions]);

  return (
    <div
      ref={containerRef}
      className="image-sweep-page"
      style={{ maxWidth: 1100, margin: '0 auto' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22 }}>🧹 Bild-Review</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['unreviewed', 'with_image', 'visible', 'hidden', 'all'] as FilterMode[]).map((f) => {
            const label =
              f === 'unreviewed'
                ? `Offen (${stats.unreviewed})`
                : f === 'with_image'
                  ? 'Mit Bild'
                  : f === 'hidden'
                    ? 'Versteckt'
                    : f === 'visible'
                      ? 'Sichtbar'
                      : 'Alle';
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  background: filter === f ? '#E03705' : '#2a2a2a',
                  color: '#fff',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Global Stats Bar */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 16,
          padding: '10px 16px',
          background: '#1a1a2e',
          borderRadius: 10,
          fontSize: 13,
          color: '#aaa',
        }}
      >
        <span>
          📊 Gesamt: <strong style={{ color: '#fff' }}>{stats.total}</strong>
        </span>
        <span>
          🖼️ Mit Bild: <strong style={{ color: '#fff' }}>{stats.withImage}</strong>
        </span>
        <span>
          📋 Offen: <strong style={{ color: '#ffd43b' }}>{stats.unreviewed}</strong>
        </span>
        <span>
          🙈 Versteckt: <strong style={{ color: '#ff6b6b' }}>{stats.hidden}</strong>
        </span>
        <span>
          👁️ Sichtbar: <strong style={{ color: '#51cf66' }}>{stats.visible}</strong>
        </span>
      </div>

      {/* Chapter Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginBottom: 16,
          overflowX: 'auto',
          paddingBottom: 4,
          scrollbarWidth: 'thin',
        }}
      >
        <button
          onClick={() => setSelectedChapter(null)}
          style={{
            padding: '6px 14px',
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            background: selectedChapter === null ? '#E03705' : '#1a1a2e',
            color: selectedChapter === null ? '#fff' : '#aaa',
          }}
        >
          Alle ({stats.withImage})
        </button>
        {chapterIds.map((ch) => {
          const cs = chapterStats.get(ch);
          if (!cs) return null;
          const reviewedPct =
            cs.total > 0
              ? Math.round(((cs.hidden + (cs.reviewed - cs.hidden)) / cs.total) * 100)
              : 0;
          const isActive = selectedChapter === ch;
          return (
            <button
              key={ch}
              onClick={() => setSelectedChapter(ch)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                whiteSpace: 'nowrap',
                position: 'relative',
                overflow: 'hidden',
                background: isActive ? '#E03705' : '#1a1a2e',
                color: isActive ? '#fff' : '#aaa',
                fontWeight: isActive ? 700 : 400,
              }}
            >
              {/* Progress bar background */}
              {!isActive && cs.hidden > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${Math.round((cs.hidden / cs.total) * 100)}%`,
                    background: 'rgba(255,107,107,0.15)',
                  }}
                />
              )}
              <span style={{ position: 'relative' }}>
                {ch}. {CHAPTER_NAMES[ch]?.split(' ')[0] || `Kap.${ch}`}
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 10,
                    opacity: 0.7,
                    color:
                      cs.hidden === cs.total
                        ? '#ff6b6b'
                        : cs.hidden > 0
                          ? '#ffd43b'
                          : isActive
                            ? '#fff'
                            : '#666',
                  }}
                >
                  {cs.hidden}/{cs.total}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Selected chapter detail stats */}
      {selectedChapter !== null && chapterStats.has(selectedChapter) && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 12,
            padding: '8px 14px',
            background: '#12122a',
            borderRadius: 8,
            fontSize: 12,
            color: '#888',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 600, color: '#ccc' }}>
            Kap. {selectedChapter}: {CHAPTER_NAMES[selectedChapter]}
          </span>
          <span>|</span>
          <span>
            Bilder:{' '}
            <strong style={{ color: '#fff' }}>{chapterStats.get(selectedChapter)!.total}</strong>
          </span>
          <span>
            Versteckt:{' '}
            <strong style={{ color: '#ff6b6b' }}>
              {chapterStats.get(selectedChapter)!.hidden}
            </strong>
          </span>
          <span>
            Sichtbar:{' '}
            <strong style={{ color: '#51cf66' }}>
              {chapterStats.get(selectedChapter)!.total - chapterStats.get(selectedChapter)!.hidden}
            </strong>
          </span>
        </div>
      )}

      {/* Progress */}
      {questions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#888',
              marginBottom: 4,
            }}
          >
            <span>
              {currentIndex + 1} / {questions.length}
            </span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 4, background: '#2a2a2a', borderRadius: 2, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #E03705, #ff6b35)',
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>Lade Fragen...</div>
      )}
      {error && (
        <div
          style={{
            padding: 12,
            background: '#3d1111',
            color: '#ff6b6b',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {!loading && current && (
        <div
          style={{
            background: '#1a1a2e',
            borderRadius: 12,
            overflow: 'hidden',
            border: current.image_hidden
              ? '2px solid #ff6b6b'
              : current.image_reviewed
                ? '2px solid #51cf66'
                : '2px solid #2a2a2e',
          }}
        >
          {/* Image */}
          {current.image_url && (
            <div style={{ position: 'relative' }}>
              <img
                src={current.image_url}
                alt="Frage-Bild"
                style={{
                  width: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  background: '#111',
                  display: 'block',
                  opacity: current.image_hidden ? 0.3 : 1,
                  transition: 'opacity 0.3s',
                }}
                loading="eager"
              />
              {current.image_hidden && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(255,107,107,0.9)',
                    color: '#fff',
                    padding: '8px 20px',
                    borderRadius: 8,
                    fontWeight: 'bold',
                    fontSize: 16,
                  }}
                >
                  🙈 VERSTECKT
                </div>
              )}
              {!current.image_hidden && current.image_reviewed && (
                <div
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    background: 'rgba(81,207,102,0.9)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 6,
                    fontWeight: 'bold',
                    fontSize: 12,
                  }}
                >
                  ✓ Geprüft
                </div>
              )}
            </div>
          )}

          {/* Question Info */}
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: '#E03705',
                  color: '#fff',
                  padding: '3px 10px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Kap. {current.chapter_id}: {CHAPTER_NAMES[current.chapter_id]}
              </span>
              <span
                style={{
                  background: '#2a2a4e',
                  color: '#aaa',
                  padding: '3px 10px',
                  borderRadius: 20,
                  fontSize: 11,
                }}
              >
                {current.type}
              </span>
              <span
                style={{
                  background: '#2a2a4e',
                  color: '#888',
                  padding: '3px 10px',
                  borderRadius: 20,
                  fontSize: 11,
                }}
              >
                {current.id}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: '#ddd' }}>
              {current.question_text_de}
            </p>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderTop: '1px solid #2a2a3e',
            }}
          >
            <button
              onClick={() => toggleImage(true)}
              disabled={saving || current.image_hidden}
              style={{
                flex: 1,
                padding: '16px 0',
                border: 'none',
                cursor: saving ? 'wait' : 'pointer',
                background: current.image_hidden ? '#1a1a1a' : '#3d1111',
                color: current.image_hidden ? '#555' : '#ff6b6b',
                fontSize: 16,
                fontWeight: 700,
                transition: 'background 0.2s',
              }}
            >
              ← Bild verbergen (A)
            </button>
            <div style={{ width: 1, background: '#2a2a3e' }} />
            <button
              onClick={keepImage}
              disabled={saving || (currentIndex >= questions.length - 1 && current.image_reviewed)}
              style={{
                flex: 1,
                padding: '16px 0',
                border: 'none',
                cursor: 'pointer',
                background: current.image_reviewed && !current.image_hidden ? '#0a2a0a' : '#113d11',
                color: '#51cf66',
                fontSize: 16,
                fontWeight: 700,
                transition: 'background 0.2s',
              }}
            >
              {current.image_reviewed && !current.image_hidden
                ? '✓ Behalten'
                : 'Bild behalten (D) →'}
            </button>
          </div>
        </div>
      )}

      {!loading && !current && questions.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          {filter === 'unreviewed' ? (
            <>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#51cf66', marginBottom: 8 }}>
                {selectedChapter !== null
                  ? `Kapitel ${selectedChapter} vollständig geprüft!`
                  : 'Alle Bilder geprüft!'}
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                {stats.hidden} versteckt · {stats.visible - stats.unreviewed} behalten
              </div>
            </>
          ) : (
            <div style={{ color: '#888' }}>
              {selectedChapter !== null
                ? `Keine Bilder in Kapitel ${selectedChapter} für diesen Filter.`
                : 'Keine Fragen in diesem Filter gefunden.'}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      {!loading && questions.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 12,
            marginTop: 16,
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #333',
              background: '#1a1a2e',
              color: '#aaa',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            ← Zurück (↑)
          </button>
          {current?.image_hidden && (
            <button
              onClick={() => toggleImage(false)}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid #555',
                background: '#2a2a4e',
                color: '#aaa',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              ↩ Rückgängig (U)
            </button>
          )}
          <button
            onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
            disabled={currentIndex >= questions.length - 1}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #333',
              background: '#1a1a2e',
              color: '#aaa',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Weiter → (↓)
          </button>
        </div>
      )}

      {/* Keyboard Hint */}
      <div
        style={{
          marginTop: 20,
          textAlign: 'center',
          fontSize: 12,
          color: '#555',
        }}
      >
        ⌨️ Tastatur: <strong>A</strong> = Verbergen · <strong>D</strong> = Behalten ·{' '}
        <strong>U</strong> = Rückgängig · <strong>↑↓</strong> = Navigation
      </div>
    </div>
  );
};
